import { Drawer, Modal } from '@prio365/prio365-react-library';
import { useQuery } from '@tanstack/react-query';
import { notification } from 'antd';
import classNames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ApiResult } from '../../../api';
import {
  getSearchConfigBySearchType,
  getUserMe,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import {
  FILTER_DATA_LIST_CLASS_PREFIX,
  FilterBar,
} from '../../../components/Filter/FilterBar';
import FilterContextProvider from '../../../components/Filter/FilterContextProvider';
import { FilterConfig } from '../../../components/Filter/types';
import Flex from '../../../components/Flex';
import { Company } from '../../../models/Company';
import { Contact } from '../../../models/Contact';
import { Project } from '../../../models/Project';
import {
  TimeRecord,
  TimeRecordSearchResultItem,
} from '../../../models/TimeRecord';
import {
  OfficeRole,
  TimeRecordsModuleContextType,
} from '../../../models/Types';
import { PrioTheme } from '../../../theme/types';
import { makePrioStyles } from '../../../theme/utils';
import useOfficesContext from '../../companies/hooks/useOfficesContext';
import { apiFetchInternalProjectContacts } from '../../projects/api';
import useProjectsContext from '../../projects/hooks/useProjectsContext';
import { deleteMyTimeRecord } from '../actions';
import { apiDeleteTimeRecords } from '../api';
import EditTimeRecord from './EditTimeRecord/EditTimeRecord';
import TimeRecordsPageTable, {
  TimeRecordsPageTableRef,
} from './TimeRecordsPageTable';
import useAccessRights2 from '../../users/hooks/useAccessRights2';

const useStyles = makePrioStyles((theme) => ({
  root: {
    padding: 24,
    flex: 1,
    height: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeadline: {
    '&.ant-typography': {
      fontWeight: theme.old.typography.fontWeight.regular,
    },
  },
  filterbar: { marginBottom: theme.spacing.regular },
  totalDisplay: {
    marginBottom: theme.spacing.regular,
    fontStyle: 'italic',
    fontSize: theme.font.fontSize.small,
  },
  editDrawer: {
    '& .prio-drawer-content': {
      padding: 0,
      overflow: 'hidden',
    },
  },
}));

interface TimeRecordsPageProps {
  className?: string;
  contextType?: TimeRecordsModuleContextType;
  officeRoles?: OfficeRole[];
  setSelectedTimeRecordsObjects?: (value: TimeRecord[]) => void;
  setTimeRecords?: (value: TimeRecord[]) => void;
  setReportDrawerVisible?: (value: boolean) => void;
  setBetaReportDrawerVisible?: (value: boolean) => void;
  setSearchString?: (value: string) => void;
}

export const TimeRecordsPage: React.FC<TimeRecordsPageProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const {
    className,
    contextType,
    officeRoles,
    setSelectedTimeRecordsObjects,
    setTimeRecords,
    setReportDrawerVisible,
    setBetaReportDrawerVisible,
    setSearchString,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector(getUserMe);

  const {
    checkOfficeRolesOfProjectId,
    checkProjectRolesOfProjectId,
    checkGlobalRoles,
  } = useAccessRights2();

  const dispatch = useDispatch();
  const { officeId, projectId } = useParams();

  const { getOfficeById } = useOfficesContext();
  const { allProjects } = useProjectsContext();

  const searchType = 'timeRecords';

  const searchFilterConfig = useSelector((state: RootReducerState) =>
    getSearchConfigBySearchType(state, searchType)
  );

  const isController = useMemo(() => {
    return (
      checkGlobalRoles(['globalController']) ||
      checkOfficeRolesOfProjectId(['officeController'], projectId) ||
      checkProjectRolesOfProjectId(['projectController'], projectId)
    );
  }, [
    projectId,
    checkGlobalRoles,
    checkOfficeRolesOfProjectId,
    checkProjectRolesOfProjectId,
  ]);

  const hasSubProjects = useMemo(() => {
    return allProjects.some((p) => projectId && p.parentProject === projectId);
  }, [allProjects, projectId]);

  const subsidaryId = useMemo(() => {
    const office = getOfficeById(officeId);
    return office?.companyId;
  }, [officeId, getOfficeById]);

  const projectIdsFromOffice = useMemo(() => {
    if (!officeId) {
      return [];
    }
    return allProjects
      .filter((p) => p.internalOfficeId?.toLowerCase() === officeId)
      .map((p) => p.projectId);
  }, [allProjects, officeId]);

  const tableRef = useRef<TimeRecordsPageTableRef>(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState<boolean>(false);
  const [clickedTimeRecord, setClickedTimeRecord] = useState<
    TimeRecord | undefined
  >(null);
  const [selectedTimeRecords, setSelectedTimeRecords] = useState<TimeRecord[]>(
    []
  );

  const [filteredTimeRecords, setFilteredTimeRecords] = useState<
    TimeRecord[] | null
  >(null);

  const [deleteSelectionModalVisible, setDeleteSelectionModalVisible] =
    useState<boolean>(false);

  const [timeRecordsSearchResultItems, setTimeRecordsSearchResultItems] =
    useState<TimeRecordSearchResultItem[]>([]);

  const customDefaultSearchParameters = useMemo(() => {
    switch (contextType) {
      case 'me':
        const defaultsMe = (searchFilterConfig?.searchableParameters || [])
          .flatMap((param: FilterConfig) => {
            return param.defaultValues.map((d: any) => ({
              parameterName: param.parameterName,
              defaultValue: d.defaultValue ?? d,
              defaultMethod: d.defaultMethod ?? 'eq',
            }));
          })
          .filter(
            (p) =>
              p.parameterName !== 'IncludeSubProjects' &&
              p.parameterName !== 'OutgoingInvoiceIds' &&
              p.parameterName !== 'IsUsedInOutgoingInvoice'
          );

        defaultsMe.push({
          parameterName: 'ContactId',
          defaultValue: userMe?.id,
          defaultMethod: 'eq',
        });

        if (projectId) {
          defaultsMe.push({
            parameterName: 'ProjectId',
            defaultValue: projectId,
            defaultMethod: 'eq',
          });
        }

        return defaultsMe;

      case 'project':
        if (!searchFilterConfig?.searchableParameters) return [];

        const parameters = isController
          ? searchFilterConfig.searchableParameters.filter(
              (param) => param.parameterName !== 'IncludeSubProjects'
            )
          : searchFilterConfig.searchableParameters;

        const defaults = parameters.flatMap((param: FilterConfig) => {
          return param.defaultValues.map((d: any) => ({
            parameterName: param.parameterName,
            defaultValue: d.defaultValue ?? d,
            defaultMethod: d.defaultMethod ?? 'eq',
          }));
        });

        defaults.push({
          parameterName: 'ProjectId',
          defaultValue: projectId,
          defaultMethod: 'eq',
        });

        return defaults;
      case 'office':
        if (!subsidaryId) {
          return [];
        }
        return [
          {
            parameterName: 'SubsidiaryId',
            defaultValue: subsidaryId,
            defaultMethod: 'eq',
          },
        ];
      default:
        return [];
    }
  }, [
    contextType,
    subsidaryId,
    projectId,
    userMe?.id,
    isController,
    searchFilterConfig,
  ]);

  const { data } = useQuery({
    queryKey: ['internalProjectContacts', projectId],
    queryFn: () => apiFetchInternalProjectContacts(projectId, true),
    staleTime: 1000 * 60, // 1 min
    enabled: !!projectId && contextType === 'project',
  });

  const projectContacts = useMemo(() => {
    return data?.data ?? [];
  }, [data]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const updateTimeRecordsTable = () => {
    tableRef.current?.fetchTimeRecords();
  };
  const onCloseEditRecordDrawer = () => {
    setIsEditDrawerOpen(false);
  };

  const handleRowClick = (timeRecord: TimeRecord) => {
    setClickedTimeRecord(timeRecord);
    setIsEditDrawerOpen(true);
  };

  const onSelectionChange = (items: TimeRecord[]) => {
    setSelectedTimeRecords(items);
    setSelectedTimeRecordsObjects?.(items);
  };

  const handleFinish = (value: TimeRecord) => {
    setIsEditDrawerOpen(false);
    setClickedTimeRecord(null);
    setTimeout(() => updateTimeRecordsTable(), 1000);
  };

  const handleModalOk = () => {
    handleDelete(selectedTimeRecords);
    setDeleteSelectionModalVisible(false);
  };

  const deleteSingleTimeRecord = (editingElement: TimeRecord) => {
    setSelectedTimeRecords([editingElement]);
    setDeleteSelectionModalVisible(true);
    setIsEditDrawerOpen(false);
  };

  const onDelete = () => {
    setDeleteSelectionModalVisible(true);
  };

  const handleDelete = async (value: TimeRecord[]) => {
    await Promise.all(
      value.map((p) =>
        apiDeleteTimeRecords(
          p.timeRecordId,
          p,
          p.contactId === userMe.id ? 'me' : contextType
        )
      )
    )
      .then((values: ApiResult<undefined>[]) => {
        values.forEach(({ data }) => {
          if (data && data['contactId'] === userMe.id) {
            dispatch(
              deleteMyTimeRecord(
                data['timeRecordId'],
                data,
                data['contactId'] === userMe.id
              )
            );
          }
        });
      })
      .catch((error) => {
        notification.open({
          message: t('common:error'),
          description: t('timeRecords:errorMessages.deleteError'),
        });
        console.error(error.message);
      });
    setSelectedTimeRecords([]);
    setIsEditDrawerOpen(false);
    setClickedTimeRecord(null);
    updateTimeRecordsTable();
  };

  const handleOnFetchSearch = () => {
    if (tableRef.current) {
      tableRef.current.resetActiveFilters();
    }
    setFilteredTimeRecords([]);
  };

  const handleOnFilterChange = (args: {
    appliedFiltersEnabled: boolean;
    data: TimeRecord[];
  }) => {
    const { appliedFiltersEnabled, data } = args;
    setFilteredTimeRecords(appliedFiltersEnabled ? data : null);
  };
  //#endregion

  //#region ------------------------------ Components
  function renderFilterBar(contextType: string): React.ReactNode {
    switch (contextType) {
      case 'me':
        return (
          <FilterBar
            className={classes.filterbar}
            hiddenPickers={[
              'ContactId',
              'IncludeSubProjects',
              'OutgoingInvoiceIds',
              'IsUsedInOutgoingInvoice',
              ...(!hasSubProjects && projectId ? ['ProjectId'] : []),
            ]}
            pickerFilterFunctions={{
              ProjectId: (project: Project) => {
                if (!projectId) {
                  return true;
                }
                return (
                  projectId === project.projectId ||
                  project.parentProject === projectId
                );
              },
              SubsidiaryId: (company: Company) => {
                return company.companyType === 'internalCompany';
              },
            }}
            onFetchSearch={handleOnFetchSearch}
          />
        );
      case 'project':
        return (
          <FilterBar
            className={classes.filterbar}
            hiddenPickers={[
              'IncludeSubProjects',
              ...(hasSubProjects ? [] : ['ProjectId']),
            ]}
            onChange={setSearchString}
            onFetchSearch={handleOnFetchSearch}
            pickerFilterFunctions={{
              ContactId: (contact: Contact) => {
                return projectContacts.some(
                  (pc) => pc.contactId === contact.contactId
                );
              },
              ProjectId: (project: Project) => {
                if (hasSubProjects) {
                  return (
                    project.projectId === projectId ||
                    project.parentProject === projectId
                  );
                }
                return project.projectId === projectId;
              },
              SubsidiaryId: (company: Company) => {
                return company.companyType === 'internalCompany';
              },
            }}
          />
        );
      case 'office':
        return (
          <FilterBar
            className={classes.filterbar}
            onChange={setSearchString}
            pickerFilterFunctions={{
              ContactId: (contact: Contact) => {
                return contact.contactType === 'InternalContact';
              },
              ProjectId: (project: Project) => {
                return projectIdsFromOffice.includes(project.projectId);
              },
              SubsidiaryId: (company: Company) => {
                return company.companyType === 'internalCompany';
              },
            }}
            onFetchSearch={handleOnFetchSearch}
          />
        );
      default: // 'global'
        return (
          <FilterBar
            onChange={setSearchString}
            onFetchSearch={handleOnFetchSearch}
            pickerFilterFunctions={{
              ContactId: (contact: Contact) => {
                return contact.contactType === 'InternalContact';
              },
              SubsidiaryId: (company: Company) => {
                return company.companyType === 'internalCompany';
              },
            }}
          />
        );
    }
  }

  const renderTotal = () => {
    const isSelection = selectedTimeRecords.length > 0;

    const idsToFilter = (
      selectedTimeRecords.length > 0
        ? selectedTimeRecords
        : (filteredTimeRecords ?? [])
    ).map((tr) => tr.timeRecordId);

    const totalLabel = isSelection
      ? t('timeRecords:totalLabel.selected')
      : t('timeRecords:totalLabel.all');

    const totalValue = timeRecordsSearchResultItems.reduce((acc, curr) => {
      if (
        idsToFilter.length === 0 ||
        idsToFilter.includes(curr.data.timeRecordId)
      ) {
        return acc + curr.calculated.hours;
      }
      return acc;
    }, 0);
    return (
      <div className={classes.totalDisplay}>
        {`${totalLabel}: ${totalValue.toLocaleString()} h`}
      </div>
    );
  };
  //#endregion

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      <FilterContextProvider
        searchType={searchType}
        customDefaultSearchParameters={customDefaultSearchParameters}
        ignoreBackendDefaultSearchParameters={
          contextType === 'me' || contextType === 'project'
        }
        transformedMap={{
          IncludeSubProjects: [],
        }}
      >
        {renderFilterBar(contextType)}
        {renderTotal()}
        <TimeRecordsPageTable
          className={FILTER_DATA_LIST_CLASS_PREFIX}
          ref={tableRef}
          tableId="trp1"
          selectedTimeRecords={selectedTimeRecords}
          onRowClick={handleRowClick}
          onSelectionChange={onSelectionChange}
          onDelete={onDelete}
          setTimeRecords={setTimeRecords}
          setReportDrawerVisible={setReportDrawerVisible}
          setBetaReportDrawerVisible={setBetaReportDrawerVisible}
          setTimeRecordSearchResultItems={setTimeRecordsSearchResultItems}
          onFilterChange={handleOnFilterChange}
        />
        <Drawer
          title={
            clickedTimeRecord?.title ??
            t('timeRecords:navigationBar.editTimeRecord')
          }
          position="right"
          closable={true}
          onClose={onCloseEditRecordDrawer}
          visible={isEditDrawerOpen}
          customWidth={theme.old.components.drawerWidth}
          className={classes.editDrawer}
        >
          <EditTimeRecord
            timeRecordId={clickedTimeRecord?.timeRecordId}
            timeRecord={clickedTimeRecord}
            onFinish={handleFinish}
            onCancel={onCloseEditRecordDrawer}
            onDelete={deleteSingleTimeRecord}
            contextType={contextType}
            officeRoles={officeRoles}
            onlyOffice={contextType !== 'global'}
            setOpen={setIsEditDrawerOpen}
          />
        </Drawer>
        <Modal
          visible={deleteSelectionModalVisible}
          onOk={() => handleModalOk()}
          onClose={() => setDeleteSelectionModalVisible(false)}
          title={t('timeRecords:modalDelete.title')}
          okText={t('timeRecords:modalDelete.confirm')}
          cancelText={t('timeRecords:modalDelete.cancel')}
        >
          {t(
            `accounting:modalDelete.content.${
              selectedTimeRecords?.length === 1 ? `single` : `multiple`
            }`,
            {
              amount: selectedTimeRecords?.length,
            }
          )}
        </Modal>
      </FilterContextProvider>
    </Flex.Column>
  );
};

export default TimeRecordsPage;
